import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * What the AI did, and asking it again (T-0239; E21-F04).
 *
 *   - GET  …/documents/:document_id/ai-runs              — the run ledger per worker
 *   - POST …/documents/:document_id/ai-runs/:worker/rerun — Read again (lead and above)
 *   - POST …/health/domains/:domain_id/recheck            — Re-check a domain (lead and above)
 *   - POST …/readiness/criteria/:criterion_id/recheck     — Re-check a criterion (lead and above)
 *
 * Every POST mints its own Idempotency-Key: a retry is a new request. The
 * Re-check hooks are here for E22 (T-0243), which places the buttons.
 */

export const AiRunSchema = z.object({
  id: z.string(),
  trigger: z.string(),
  status: z.string(),
  reason: z.string().nullable(),
  proposals_created: z.number(),
  proposals_superseded: z.number(),
  document_version_id: z.string().nullable(),
  target_kind: z.string().nullable(),
  target_id: z.string().nullable(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
});
export type AiRun = z.infer<typeof AiRunSchema>;

export const AiRunProposalSchema = z.object({
  id: z.string(),
  proposal_type: z.string(),
  status: z.string(),
  headline: z.string().nullable(),
  created_at: z.string(),
});
export type AiRunProposal = z.infer<typeof AiRunProposalSchema>;

export const DocumentAiWorkerSchema = z.object({
  worker: z.string(),
  label: z.string(),
  /** contract_compliance | health_check | go_live_readiness, or null. */
  module: z.string().nullable(),
  latest: AiRunSchema,
  history: z.array(AiRunSchema),
  proposals: z.array(AiRunProposalSchema),
  proposals_total: z.number(),
  can_read_again: z.boolean(),
});
export type DocumentAiWorker = z.infer<typeof DocumentAiWorkerSchema>;

export const DocumentAiRunsSchema = z.object({
  document_id: z.string(),
  workers: z.array(DocumentAiWorkerSchema),
});
export type DocumentAiRuns = z.infer<typeof DocumentAiRunsSchema>;

const RequestedSchema = z.object({ requested: z.literal(true) }).passthrough();
export type Requested = z.infer<typeof RequestedSchema>;

function newIdempotencyKey(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const enc = encodeURIComponent;

function documentRunsPath(engagementId: string, documentId: string): string {
  return `/api/v1/engagements/${enc(engagementId)}/documents/${enc(documentId)}/ai-runs`;
}

export function getDocumentAiRuns(
  engagementId: string,
  documentId: string,
): Promise<DocumentAiRuns> {
  return apiClient(documentRunsPath(engagementId, documentId), DocumentAiRunsSchema);
}

export function documentAiRunsKey(
  engagementId: string | undefined,
  documentId: string | undefined,
) {
  return ['document-ai-runs', engagementId, documentId] as const;
}

/** Every worker that looked at the document. Polls while a read is running or was just asked for. */
export function useDocumentAiRunsQuery(
  engagementId: string | undefined,
  documentId: string | undefined,
  opts: { poll?: boolean } = {},
) {
  return useQuery({
    queryKey: documentAiRunsKey(engagementId, documentId),
    queryFn: () => getDocumentAiRuns(engagementId!, documentId!),
    enabled: Boolean(engagementId && documentId),
    refetchInterval: (query) => {
      const running = query.state.data?.workers.some((w) => w.latest.status === 'running');
      return opts.poll || running ? 5_000 : false;
    },
  });
}

export function readDocumentAgain(
  engagementId: string,
  documentId: string,
  worker: string,
): Promise<Requested> {
  return apiClient(
    `${documentRunsPath(engagementId, documentId)}/${enc(worker)}/rerun`,
    RequestedSchema,
    {
      method: 'POST',
      idempotencyKey: newIdempotencyKey(),
    },
  );
}

/** Read again for one worker; the card refreshes once the request is queued. */
export function useReadAgainMutation(engagementId: string, documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (worker: string) => readDocumentAgain(engagementId, documentId, worker),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: documentAiRunsKey(engagementId, documentId) });
    },
  });
}

export function recheckHealthDomain(engagementId: string, domainId: string): Promise<Requested> {
  return apiClient(
    `/api/v1/engagements/${enc(engagementId)}/health/domains/${enc(domainId)}/recheck`,
    RequestedSchema,
    { method: 'POST', idempotencyKey: newIdempotencyKey() },
  );
}

export function recheckReadinessCriterion(
  engagementId: string,
  criterionId: string,
): Promise<Requested> {
  return apiClient(
    `/api/v1/engagements/${enc(engagementId)}/readiness/criteria/${enc(criterionId)}/recheck`,
    RequestedSchema,
    { method: 'POST', idempotencyKey: newIdempotencyKey() },
  );
}

/** Re-check one health domain: the domain judge re-reads all its evidence. */
export function useRecheckHealthDomainMutation(engagementId: string) {
  return useMutation({
    mutationFn: (domainId: string) => recheckHealthDomain(engagementId, domainId),
  });
}

/** Re-check one go-live criterion: the criterion judge re-reads all its evidence. */
export function useRecheckReadinessCriterionMutation(engagementId: string) {
  return useMutation({
    mutationFn: (criterionId: string) => recheckReadinessCriterion(engagementId, criterionId),
  });
}
